import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Modal,
  Loader,
  Alert,
  TextInput,
} from '@mantine/core';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconAlertCircle, IconSearch, IconPin } from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import {
  useJournalEntries,
  useCreateJournalEntry,
  useUpdateJournalEntry,
  useDeleteJournalEntry,
  useToggleJournalPin,
} from '@/hooks/useJournal';
import { JournalEntryForm } from '@/features/journal/JournalEntryForm';
import { JournalEntryCard } from '@/features/journal/JournalEntryCard';
import { EmptyJournalState } from '@/features/journal/EmptyJournalState';
import type { JournalEntry, JournalEntryCreate } from '@/types/journal';
import { useIsMobile } from '@/hooks/useIsMobile';

export default function JournalPage() {
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const { data, isLoading, error } = useJournalEntries({
    search: debouncedSearch || undefined,
  });

  const createMutation = useCreateJournalEntry();
  const updateMutation = useUpdateJournalEntry();
  const deleteMutation = useDeleteJournalEntry();
  const togglePinMutation = useToggleJournalPin();

  const handleOpenCreate = () => {
    setEditingEntry(null);
    openForm();
  };

  const handleEdit = (entry: JournalEntry) => {
    setEditingEntry(entry);
    openForm();
  };

  const handleDeleteClick = (entryId: string) => {
    closeForm();
    setEditingEntry(null);
    setDeleteConfirmId(entryId);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId, {
        onSettled: () => {
          setDeleteConfirmId(null);
        },
      });
    }
  };

  const handleFormSubmit = (formData: JournalEntryCreate) => {
    if (editingEntry) {
      updateMutation.mutate(
        { id: editingEntry.id, data: formData },
        { onSuccess: () => closeForm() }
      );
    } else {
      createMutation.mutate(formData, { onSuccess: () => closeForm() });
    }
  };

  const handleFormCancel = () => {
    closeForm();
    setEditingEntry(null);
  };

  const handleTogglePin = (entryId: string) => {
    togglePinMutation.mutate(entryId);
  };

  // Split entries into pinned and unpinned
  const { pinnedEntries, recentEntries } = useMemo(() => {
    if (!data?.entries) return { pinnedEntries: [], recentEntries: [] };
    return {
      pinnedEntries: data.entries.filter((e) => e.pinned),
      recentEntries: data.entries.filter((e) => !e.pinned),
    };
  }, [data?.entries]);

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to view its journal" />
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title order={1}>Tank Journal</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
            {isMobile ? 'Add' : 'Add Entry'}
          </Button>
        </Group>

        {/* Search */}
        <TextInput
          placeholder="Search entries..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {/* Error State */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error instanceof Error ? error.message : 'Failed to load journal entries'}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <Group justify="center" py="xl">
            <Loader role="status" />
          </Group>
        )}

        {/* Content */}
        {!isLoading && !error && data && (
          <>
            {data.entries.length === 0 ? (
              debouncedSearch ? (
                <Text c="dimmed" ta="center" py="xl">
                  No entries match your search.
                </Text>
              ) : (
                <EmptyJournalState onAddClick={handleOpenCreate} />
              )
            ) : (
              <Stack gap="sm">
                {/* Pinned section */}
                {pinnedEntries.length > 0 && (
                  <>
                    <Group gap="xs">
                      <IconPin size={14} />
                      <Text size="sm" fw={500} c="dimmed" tt="uppercase">
                        Pinned
                      </Text>
                    </Group>
                    {pinnedEntries.map((entry) => (
                      <JournalEntryCard
                        key={entry.id}
                        entry={entry}
                        onEdit={handleEdit}
                        onTogglePin={handleTogglePin}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </>
                )}

                {/* Recent section */}
                {recentEntries.length > 0 && (
                  <>
                    {pinnedEntries.length > 0 && (
                      <Text size="sm" fw={500} c="dimmed" tt="uppercase" mt="sm">
                        Recent
                      </Text>
                    )}
                    {recentEntries.map((entry) => (
                      <JournalEntryCard
                        key={entry.id}
                        entry={entry}
                        onEdit={handleEdit}
                        onTogglePin={handleTogglePin}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={formOpened}
        onClose={handleFormCancel}
        title={editingEntry ? 'Edit Entry' : 'New Journal Entry'}
        size="lg"
      >
        <JournalEntryForm
          entry={editingEntry || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          onDelete={handleDeleteClick}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deleteConfirmId}
        onClose={() => {
          if (!deleteMutation.isPending) {
            setDeleteConfirmId(null);
          }
        }}
        title="Delete Entry"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this journal entry? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => setDeleteConfirmId(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteConfirm}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
